import { Button, FileButton, Group, Image, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconPhoto, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useRef } from "react";

interface FileDropProps {
  /** Currently selected file (controlled by the parent). */
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  accept?: string;
}

/**
 * Unified image picker. Owns the object-URL preview lifecycle so pages only
 * keep the `File` in state. Click anywhere on the area to choose/replace.
 */
export default function FileDrop({ value, onChange, disabled, accept = "image/*" }: FileDropProps) {
  const resetRef = useRef<() => void>(null);

  // Object URL derived from the file; revoked when it changes or on unmount.
  const preview = useMemo(() => (value ? URL.createObjectURL(value) : null), [value]);
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  const clear = () => {
    onChange(null);
    resetRef.current?.();
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Image
      </Text>

      <FileButton resetRef={resetRef} onChange={onChange} accept={accept} disabled={disabled}>
        {(props) => (
          <UnstyledButton {...props} disabled={disabled} className="dropzone" p="lg">
            {preview ? (
              <Group gap="md" wrap="nowrap" align="center">
                <Image
                  src={preview}
                  w={72}
                  h={72}
                  radius="md"
                  style={{ objectFit: "cover", flexShrink: 0 }}
                />
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {value?.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Click to replace
                  </Text>
                </Stack>
              </Group>
            ) : (
              <Group gap="md" wrap="nowrap" align="center" justify="center" py="md">
                <IconPhoto size={28} stroke={1.5} color="var(--mantine-color-dimmed)" />
                <Stack gap={2} align="center">
                  <Text size="sm" fw={500}>
                    Choose an image
                  </Text>
                  <Text size="xs" c="dimmed">
                    PNG, JPG or HEIC
                  </Text>
                </Stack>
              </Group>
            )}
          </UnstyledButton>
        )}
      </FileButton>

      {value && !disabled && (
        <Group justify="flex-end">
          <Button variant="subtle" color="red" size="xs" leftSection={<IconX size={14} />} onClick={clear}>
            Remove
          </Button>
        </Group>
      )}
    </Stack>
  );
}
